import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import {
  ButtonDS,
  DownloadOutlined,
  LaunchPage,
  MaterialUiColumnsProps,
  MaterialUiTable,
  sortByDateTime,
  TextCapitalized,
  triggerToast,
  usePersistMuiTableState,
} from "@qivia/ui";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import { selectCompany } from "../../homeSlice";
import {
  depositExportAsync,
  depositsListAsync,
  documentDownloadAsync,
  selectDepositExportLink,
  selectDepositExportStatus,
  selectDepositsList,
  selectDepositsListStatus,
} from "../billingsSlice";
import { PublicDeposits, PublicDepositsDisplayed } from "../billingsAPI";
import "../../../../i18n/i18n";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { documentDownloadXlsxAsync } from "../../../../libSlice/downloadFileXslx";

export const DepositTab = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const params = useParams();
  const company = useAppSelector(selectCompany);

  const [depositsListSorted, setDepositsListSorted] = useState<
    PublicDepositsDisplayed[] | null
  >([]);
  const depositsListStatus = useAppSelector(selectDepositsListStatus);
  const depositsListInit: PublicDeposits[] = useAppSelector(selectDepositsList);
  const depositExportLink = useAppSelector(selectDepositExportLink);
  const depositsExportStatus = useAppSelector(selectDepositExportStatus);

  const pageName = "deposits";
  const apiRef = useGridApiRef();
  const key = "customers-grid";

  const aggregation = {
    model: {
      amount: "sum",
      fileName: "size",
      date: "size",
      createdAt: "size",
      bucketFileNameDownload: "size",
    },
  };

  const dataGridState = usePersistMuiTableState(
    apiRef,
    key,
    pageName,
    aggregation,
  );

  const depositsList: PublicDeposits[] = useMemo(() => {
    const rowsWithId = depositsListInit.map((deposit) => {
      const newDict: PublicDeposits & { id: string } = {
        ...deposit,
        id: deposit.fileName,
      };
      return newDict;
    });
    return sortByDateTime(rowsWithId, "desc");
  }, [depositsListInit]);

  useEffect(() => {
    if (company && depositsListStatus === "idle") {
      void dispatch(depositsListAsync(company.uuid));
    }
  }, [dispatch, company, depositsListStatus]);

  useEffect(() => {
    if (depositsExportStatus === "success") {
      triggerToast(t("billings.deposits.upload.success") || "", "valid");
    } else if (depositsExportStatus === "failed") {
      triggerToast(t("billings.deposits.upload.failure") || "", "error");
    }
  }, [depositsExportStatus, t]);

  useEffect(() => {
    if (depositsList && depositsListStatus === "success") {
      const depositsListSorted = sortByDateTime(depositsList, "desc");
      setDepositsListSorted(depositsListSorted);
    }
  }, [depositsListStatus, depositsList]);

  useEffect(() => {
    if (depositExportLink.presignedUrl && depositExportLink.fileName) {
      void dispatch(documentDownloadAsync(depositExportLink));
    }
  }, [depositExportLink, dispatch]);

  const columnsDisplayed: {
    [index in keyof PublicDepositsDisplayed]: MaterialUiColumnsProps;
  } = useMemo(() => {
    return {
      amount: {
        type: "number",
        width: 100,
        valueFormatter: (value: number) => {
          return `${value} € TTC`;
        },
      },
      fileName: {
        type: "string",
        width: 200,
      },
      date: {
        type: "date",
        width: 200,
        valueFormatter: (value: string) => {
          return <TextCapitalized>{value}</TextCapitalized>;
        },
      },
      createdAt: {
        type: "date",
        width: 200,
        valueFormatter: (value: string) => {
          return <TextCapitalized>{value}</TextCapitalized>;
        },
      },
      bucketFileNameDownload: {
        type: "string",
        width: 200,
        valueFormatter: (value: string, row) => {
          return (
            value && (
              <StyledColumnButton>
                <ButtonDS
                  sizeButton={"S"}
                  format={"hug"}
                  buttonType={"secondary"}
                  singleIcon={{ icon: <DownloadOutlined />, size: "S" }}
                  onClick={() => {
                    void dispatch(
                      depositExportAsync({
                        bucketFileName: value ?? "",
                        fileName:
                          "fileName" in row ? (row["fileName"] as string) : "",
                      }),
                    );
                  }}
                />
              </StyledColumnButton>
            )
          );
        },
      },
    };
  }, [dispatch]);

  const downloadViewAction = useCallback(
    (file: Buffer | undefined) => {
      if (!file) return;
      void dispatch(
        documentDownloadXlsxAsync({
          buffer: file,
          fileName: "QIVIA_" + t("deposits.title") + ".xlsx",
        }),
      );
    },
    [t, dispatch],
  );

  if (params.tab !== "deposits") {
    return;
  }

  const searchBarProps = {
    values: depositsList,
    setFilterValues: setDepositsListSorted,
    keysToIgnore: [
      "bucketFileNameDownload" as const,
      "createdAt" as const,
      "fileName" as const,
    ],
  };

  if (!depositsListSorted) return <LaunchPage hasBorderRadius={true} />;

  return (
    <MaterialUiTable<keyof PublicDepositsDisplayed, PublicDepositsDisplayed>
      rows={depositsListSorted}
      columns={columnsDisplayed}
      pageName={pageName}
      dataGridState={dataGridState}
      searchBar={searchBarProps}
      apiRef={apiRef}
      downloadViewAction={downloadViewAction}
    />
  );
};

const StyledColumnButton = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
`;
